import {EditorView} from '@codemirror/view'
import {h} from 'hastscript'

import {buildToggledTasklistPrefixSyntax, parseTasklistPrefix} from '../../language-tools/markdown/list-utils'
import {HastElementWidget} from './hast-element'

type TaskListCheckboxWidgetProps = {
  checked: boolean
  from: number
  to: number
}

function createCheckboxNode(checked: boolean) {
  return h('input', {
    type: 'checkbox',
    tabindex: -1,
    checked: checked ? true : undefined,
    'aria-label': checked ? 'Checked task list item' : 'Unchecked task list item',
    'data-task-list-checkbox': '',
  })
}

export class TaskListCheckboxWidget extends HastElementWidget {
  #checked: boolean
  #from: number
  #to: number
  #view?: EditorView
  #input?: HTMLInputElement

  constructor(props: TaskListCheckboxWidgetProps) {
    super(createCheckboxNode(props.checked))
    this.#checked = props.checked
    this.#from = props.from
    this.#to = props.to
  }

  #onMouseDown = (event: MouseEvent) => {
    event.preventDefault()
  }

  #onChange = (event: Event) => {
    event.preventDefault()
  }

  #onClick = (event: MouseEvent) => {
    event.preventDefault()

    const view = this.#view
    const input = this.#input
    if (!view || !input) return

    const currentPrefix = view.state.doc.sliceString(this.#from, this.#to)
    const currentlyChecked = parseTasklistPrefix(currentPrefix)?.value.toLowerCase() === 'x'
    const nextChecked = !currentlyChecked

    input.checked = nextChecked
    input.setAttribute('aria-label', nextChecked ? 'Checked task list item' : 'Unchecked task list item')

    this.#dispatchToggle(view, nextChecked)
  }

  #removeEventListeners(input?: HTMLInputElement) {
    if (!input) return

    input.removeEventListener('mousedown', this.#onMouseDown)
    input.removeEventListener('change', this.#onChange)
    input.removeEventListener('click', this.#onClick)
  }

  #dispatchToggle(view: EditorView, nextChecked: boolean) {
    const currentPrefix = view.state.doc.sliceString(this.#from, this.#to)
    const charAfterPrefix = view.state.doc.sliceString(this.#to, this.#to + 1)
    const nextPrefix = buildToggledTasklistPrefixSyntax(nextChecked, currentPrefix, charAfterPrefix)

    view.dispatch({
      changes: {from: this.#from, to: this.#to, insert: nextPrefix},
      selection: view.state.selection,
    })
  }

  override toDOM(view: EditorView) {
    this.#view = view

    const input = super.toDOM(view) as HTMLInputElement

    if (this.#input && this.#input !== input) {
      this.#removeEventListeners(this.#input)
    }

    this.#input = input
    input.tabIndex = -1
    input.checked = this.#checked
    input.setAttribute('aria-label', this.#checked ? 'Checked task list item' : 'Unchecked task list item')
    input.setAttribute('data-task-list-from', String(this.#from))
    input.setAttribute('data-task-list-to', String(this.#to))

    input.addEventListener('mousedown', this.#onMouseDown)
    input.addEventListener('change', this.#onChange)
    input.addEventListener('click', this.#onClick)

    return input
  }

  override destroy(dom: HTMLElement) {
    if (dom instanceof HTMLInputElement) {
      this.#removeEventListeners(dom)
      if (dom === this.#input) this.#input = undefined
    }
    this.#view = undefined
  }

  override eq(other: HastElementWidget) {
    if (!(other instanceof TaskListCheckboxWidget)) return false
    return other.#from === this.#from && other.#to === this.#to && other.#checked === this.#checked
  }

  override updateDOM(dom: HTMLElement) {
    if (!(dom instanceof HTMLInputElement)) return false

    dom.checked = this.#checked
    dom.setAttribute('aria-label', this.#checked ? 'Checked task list item' : 'Unchecked task list item')

    return true
  }

  override ignoreEvent() {
    return true
  }
}

export type {TaskListCheckboxWidgetProps}
